import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { prop } from 'ramda';

import { Logo } from 'js/app/components/common/Logo';
import { Icon } from 'js/components/common/Icon';
import { SvgIcon } from 'js/app/components/common/SvgIcon';
import { Buttons, ErrorFallback } from 'js/components/common/Popup/ShallowPopup';

import { useFocus } from 'js/hooks/useFocus';
import { clsx } from 'js/app/utils/clsx.util';
import { useNavigate } from 'js/hooks/navigate';
import { clsb } from 'js/utils/classNames.util';
import { useSmartTranslations } from 'js/hooks/i18n';
import { usePortraitOrientation } from 'js/app/hooks/device/hooks/useMediaQuery';
import { useDeviceSelector } from 'js/store/hooks';

import popupStyles from 'js/components/common/Popup/Popup.scss';
import loginStyles from 'js/components/common/Popup/buzz/LoginPopup.scss';

export * from 'js/components/common/Popup/ShallowPopup';

/** core fn */
export const Controls = ({ onPrev, onNext, label, translate }) => {
    const rightIcon = onNext ? 'circleArrowRight' : 'circleArrowRightDisabled';
    const leftIcon = onPrev ? 'circleArrowLeft' : 'circleArrowLeftDisabled';

    return (
        <div className={popupStyles.controls}>
            <Icon
                type={leftIcon}
                onClick={onPrev}
                aria-label={translate('playtech.mobile.system.popup.aria-label.arrow.left')}
            />
            <div>
                {label}
            </div>
            <Icon
                type={rightIcon}
                onClick={onNext}
                aria-label={translate('playtech.mobile.system.popup.aria-label.arrow.right')}
            />
        </div>
    );
};

/**
 * ShallowPopup
 * @description General popup layout block. While being popup it does not center itself or have any overlays. Acts as
 * base component for conventional popups, providing some common layout, buttons and icons handling.
 */
export const ShallowPopup = ({
    id,
    onClose,
    buttons,
    children,
    controls,
    title = '',
    onTouchStart,
    className = '',
    classNames = {},
    isModal = false,
    style: popupStyle,
    iconType = 'close-popup',
    refs: { popupRef, innerPopupRef } = {},

    // buzz props
    withLogo,
    fullscreen,
    isTextCenter,
    isCustomLogin,
    showTitle = true,
    isFontIcon = true,
    isVerticalButtonAlign = true
}) => {
    const [titleRef, setFocus] = useFocus();
    const translate = useSmartTranslations();
    const style = isCustomLogin ? loginStyles : popupStyles;
    const buildCls = clsb([style, classNames]);

    useEffect(() => setFocus(), [setFocus]);

    /* buzz start */
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isMobileView = useDeviceSelector(prop('isMobile'));
    const buttonsData = typeof buttons === 'function'
        ? buttons({ onClose })
        : buttons;

    const containerRefBare = useRef();
    const containerRef = popupRef || containerRefBare;

    const isInPortrait = usePortraitOrientation();
    const isLogoVisible = isMobileView
        ? withLogo && isInPortrait
        : withLogo;

    useEffect(() => {
        const buttonsCloseModal = Array.from(containerRef.current?.querySelectorAll('.fn-close-modal') || []);

        const listener = (event) => {
            event.preventDefault();

            onClose();

            if (event.target.pathname !== pathname) {
                navigate(event.target.pathname);
            }
        };

        buttonsCloseModal.forEach(btn => btn.addEventListener('click', listener));

        return () => {
            buttonsCloseModal.forEach(btn => btn.removeEventListener('click', listener));
        };
    }, [containerRef, children, onClose, pathname, navigate]);

    const IconClose = isFontIcon ? SvgIcon : Icon;
    /* buzz end */

    return (
        <div
            id={id}
            ref={containerRef}
            style={popupStyle}
            onTouchStart={onTouchStart}
            data-testid="Popup__popup"
            className={clsx(
                'Popup__popup',
                style.popup,
                className,
                {
                    [style.withLogo]: isLogoVisible,
                    [style.fullscreen]: fullscreen,
                    [style.isTextCenter]: isTextCenter,
                    [buildCls(['isModal'])]: isModal
                }
            )}
        >
            <div className={buildCls(['header'])}>
                {isLogoVisible && <Logo isCenter className={style.popupLogo} size={isMobileView ? '5rem' : '8rem'} />}
                {showTitle && title && (
                    <div
                        tabIndex="-1"
                        ref={titleRef}
                        data-testid="ShallowPopup__title"
                        className={`popup-modal__title ${buildCls(['title'])}`}
                    >
                        {translate(title)}
                    </div>
                )}
            </div>
            <div className={`popup-modal__inner-content ${buildCls(['inner'])}`} ref={innerPopupRef}>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <>
                        {typeof children === 'string' ? <p dangerouslySetInnerHTML={{ __html: children }}/> : children}
                    </>
                </ErrorBoundary>
            </div>
            <Buttons
                buttons={buttonsData}
                className={clsx(
                    classNames.actionButtons,
                    isVerticalButtonAlign && buildCls(['verticalButtons'])
                )}
            />
            {!isModal && (
                <IconClose
                    type={iconType}
                    onClick={onClose}
                    className={clsx(buildCls(['close']), 'popup-modal__close-icon')}
                    data-testid="ShallowPopup__close"
                    aria-label={translate('playtech.mobile.system.popup.aria-label.close')}
                />
            )}
            {Boolean(controls) && <Controls translate={translate} {...controls} />}
        </div>
    );
};
